import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { cn, ColoredTileIndex, indexColorMap } from '@/lib/utils';
import ArrowCheckAqua from '@/assets/icons/arrow_check_aqua.svg?react';
import RedCross from '@/assets/icons/red_cross.svg?react';
import PrimaryAccountLogo from '@/assets/icons/primary_account_logo.svg?react';
import useOpenApiClient from '@/hooks/useOpenApiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { useEffect } from 'react';

const formSchema = z.object({
  nickname: z
    .string()
    .min(1, 'Nickname is required')
    .max(30, 'Nickname must be less than 30 characters'),
  is_primary: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface AccountDetailsSheetProps {
  onClose: () => void;
  accountId: string | null;
}

export default function AccountDetailsSheet(props: AccountDetailsSheetProps) {
  const { apiFetch } = useOpenApiClient();
  const queryClient = useQueryClient();

  const selectedAccountId = props.accountId;

  const accountsQuery = useQuery({
    queryKey: ['v1', 'accounts'],
    queryFn: () => apiFetch?.GET('/accounts'),
    select: (data) => data.data?.data,
  });

  const accountDetailsQuery = useQuery({
    queryKey: ['v1', 'accounts', selectedAccountId],
    queryFn: () =>
      apiFetch?.GET(`/accounts/{account_id}`, {
        params: {
          path: {
            account_id: selectedAccountId as string,
          },
        },
      }),
    select: (data) => {
      return data.data?.data;
    },
    enabled: !!selectedAccountId,
  });

  const accountNickNameMutation = useMutation({
    mutationKey: ['v1', 'accounts'],
    mutationFn: async (data: FormValues) => {
      if (!selectedAccountId) {
        return;
      }
      const apiPromises = [];
      if (data.nickname !== accountDetailsQuery?.data?.nickname) {
        apiPromises.push(
          apiFetch.PATCH(`/accounts/{account_id}/nickname`, {
            params: {
              path: { account_id: selectedAccountId },
            },
            body: { nickname: data.nickname },
          }),
        );
      }
      if (data.is_primary !== accountDetailsQuery?.data?.is_primary) {
        apiPromises.push(
          apiFetch.PATCH(`/accounts/{account_id}/primary`, {
            params: {
              path: { account_id: selectedAccountId },
            },
          }),
        );
      }

      return Promise.allSettled(apiPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['v1', 'accounts'],
        refetchType: 'active',
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: ['v1', 'accounts', selectedAccountId],
        refetchType: 'active',
        exact: true,
      });
      props.onClose();
    },
  });

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(
      formSchema.superRefine((data, ctx) => {
        const existingNicknames = accountsQuery.data
          ?.filter((account) => account.id !== selectedAccountId)
          ?.map((account) => account.nickname.toLowerCase());

        if (existingNicknames?.includes(data.nickname.toLowerCase())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Nickname must be unique accross all accounts',
            path: ['nickname'],
          });
        }
      }),
    ),
    values: accountDetailsQuery?.data,
    defaultValues: {
      nickname: '',
      is_primary: false,
    },
  });

  const { formState, reset: resetForm } = form;

  function onSubmit(data: FormValues) {
    accountNickNameMutation.mutate(data);
  }

  const selectedAccountIndexInAccounts =
    accountsQuery.data?.findIndex((account) => account.id === selectedAccountId) ?? 0;

  useEffect(() => {
    if (!selectedAccountId) {
      resetForm();
    }
  }, [selectedAccountId, resetForm]);

  return (
    <Sheet open={Boolean(selectedAccountId)} onOpenChange={props.onClose}>
      <SheetContent
        hideClose
        fadeOverlay
        side="bottom"
        className="bg-primary-soft-peach px-8 pt-4 pb-7"
      >
        <SheetHeader className="space-y-1 text-left">
          <SheetTitle className="flex items-center justify-between">
            <span className="text-foreground text-2xl font-thin">Account details</span>
            {form.getValues('is_primary') ? (
              <PrimaryAccountLogo className="h-14 w-14" />
            ) : (
              <span
                className={cn(
                  'border-primary-dark text-foreground flex h-14 w-14 rotate-[-9.8deg] items-center justify-center rounded-full border text-5xl leading-[46.047px] font-normal lowercase opacity-80 shadow-[-2px_4px_0px_#3C451D]',
                  indexColorMap.index[(selectedAccountIndexInAccounts % 12) as ColoredTileIndex],
                )}
              >
                {accountDetailsQuery?.data?.nickname?.[0]}
              </span>
            )}
          </SheetTitle>
          <p className="text-foreground text-lg leading-4 font-normal opacity-70">
            Explain which account this is.
          </p>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="nickname"
              render={({ field }) => (
                <FormItem className="flex flex-col py-6">
                  <FormLabel className="text-foreground pb-3 text-base leading-none font-bold">
                    NICKNAME
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder='e.g. "Fresh"'
                        className={cn(
                          'rounded-[10.415px] border-[1.302px] border-[rgba(60,69,29,0.26)] px-5 py-4',
                          'text-xl font-normal',
                          'h-14 pr-10',
                          'placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0',
                        )}
                        {...field}
                      />
                      {formState.isValid ? (
                        <ArrowCheckAqua className="absolute top-1/2 right-3 -translate-y-1/2" />
                      ) : (
                        <RedCross className="absolute top-1/2 right-3 -translate-y-1/2" />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {accountDetailsQuery?.data?.type === 'debit_card' && (
              <FormField
                control={form.control}
                name="is_primary"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Switch
                        disabled={accountDetailsQuery?.data?.is_primary}
                        id="primary-card"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:bg-primary-mid"
                      />
                    </FormControl>
                    <FormLabel className="text-primary-foreground mt-0! text-base leading-[21px] font-normal">
                      {accountDetailsQuery?.data?.is_primary
                        ? 'Primary Enrich card'
                        : 'Make Primary Enrich card'}
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <div className="border-t-primary-dark-o-1 mt-4 flex justify-center gap-3 border-t pt-5">
              <Button
                type="submit"
                variant="enrich_dialog"
                disabled={
                  !formState.isValid || !formState.isDirty || accountNickNameMutation.isPending
                }
                className="w-full max-w-3xs"
              >
                Save
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
